import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import type { EventView } from '@goaltree/backend/convex/events'
import { EVENT_DEFAULT_MINUTES, eventEndMs } from '@goaltree/backend/convex/lib/events'
import type { OptimisticLocalStore } from 'convex/browser'

// Optimistic edits for every cached events.range / events.upcoming / events.get
// result, so Today, Coming up and the Events tab react before the server does.

type EventPatch = {
  title?: string
  startsAt?: number
  allDay?: boolean
  durationMinutes?: number
  location?: string
  notes?: string
  remindMinutesBefore?: number | null
}

const inWindow = (e: EventView, from: number, to: number) => e.endsAt > from && e.startsAt < to
const byStart = (a: EventView, b: EventView) => a.startsAt - b.startsAt

function eachList(
  store: OptimisticLocalStore,
  fn: (list: EventView[], from: number, to: number) => EventView[],
) {
  for (const { args, value } of store.getAllQueries(api.events.range)) {
    if (value) store.setQuery(api.events.range, args, fn(value, args.from, args.to))
  }
  for (const { args, value } of store.getAllQueries(api.events.upcoming)) {
    if (value)
      store.setQuery(api.events.upcoming, args, {
        ...value,
        events: fn(value.events, args.from, args.to),
      })
  }
}

function applyPatch(e: EventView, p: EventPatch): EventView {
  const allDay = p.allDay ?? e.allDay
  const next = {
    ...e,
    ...(p.title !== undefined && { title: p.title.trim() || e.title }),
    ...(p.startsAt !== undefined && { startsAt: p.startsAt }),
    ...(p.location !== undefined && { location: p.location.trim() || null }),
    ...(p.notes !== undefined && { notes: p.notes.trim() || null }),
    ...(p.remindMinutesBefore !== undefined && { remindMinutesBefore: p.remindMinutesBefore }),
    allDay,
    durationMinutes: allDay
      ? null
      : (p.durationMinutes ?? e.durationMinutes ?? EVENT_DEFAULT_MINUTES),
  }
  return { ...next, endsAt: eventEndMs(next) }
}

export function optimisticCreateEvent(
  store: OptimisticLocalStore,
  a: EventPatch & { title: string; startsAt: number },
  // Converting a task keeps its id; brand-new events get a placeholder.
  id?: Id<'nodes'>,
) {
  const base: EventView = {
    _id: id ?? (`optimistic-${a.startsAt}-${a.title}` as Id<'nodes'>),
    title: a.title,
    startsAt: a.startsAt,
    endsAt: a.startsAt,
    allDay: false,
    durationMinutes: null,
    location: null,
    notes: null,
    remindMinutesBefore: null,
    goal: null,
    travel: null,
  }
  const event = applyPatch(base, a)
  eachList(store, (list, from, to) =>
    inWindow(event, from, to) ? [...list, event].sort(byStart) : list,
  )
}

export function optimisticUpdateEvent(
  store: OptimisticLocalStore,
  eventId: Id<'nodes'>,
  patch: EventPatch,
) {
  let updated: EventView | null = null
  const current = store.getQuery(api.events.get, { eventId })
  if (current) {
    updated = applyPatch(current, patch)
    store.setQuery(api.events.get, { eventId }, updated)
  }
  eachList(store, (list, from, to) => {
    const found = list.find((e) => e._id === eventId)
    const next = found ? applyPatch(found, patch) : updated
    const rest = list.filter((e) => e._id !== eventId)
    return next && inWindow(next, from, to) ? [...rest, next].sort(byStart) : rest
  })
}

export function optimisticRemoveEvent(store: OptimisticLocalStore, eventId: Id<'nodes'>) {
  eachList(store, (list) => list.filter((e) => e._id !== eventId))
}
